/**
 * Admin LiveKit fleet monitor — API client.
 * Backend: GET /admin/livekit/monitor/
 */

import authService from "../../../utils/authService";
import { decryptJSON } from "../../../utils/encryptionUtils";

export interface HostMemory {
  total_mb: number;
  used_mb: number;
  available_mb: number;
  percent: number;
}

export interface HostProcessRow {
  pid?: number;
  name?: string;
  cpu_percent?: number;
  rss_mb?: number;
}

export interface HostMetrics {
  ok: boolean;
  source?: string;
  hostname?: string;
  platform?: string;
  cpu_count?: number;
  cpu_percent?: number;
  cpu_per_core?: number[];
  load_avg?: number[];
  memory?: HostMemory;
  uptime_seconds?: number;
  processes?: HostProcessRow[];
  note?: string;
  error?: string;
  remote_url?: string;
  collected_at?: string;
}

export interface MonitorSummary {
  livekit_configured: boolean;
  sfu_reachable: boolean;
  sfu_error?: string | null;
  credential_source?: string;
  active_sfu_rooms: number;
  active_lms_livekit_sessions: number;
  total_live_participants: number;
  recording_sessions: number;
  stale_lms_sessions: number;
  mismatch_rooms: number;
}

export interface MonitorRoomRow {
  room_name: string;
  sid?: string | null;
  num_participants: number;
  num_publishers: number;
  max_participants?: number;
  creation_time?: number;
  active_recording_sfu?: boolean;
  lms_session_id?: string | null;
  session_name?: string | null;
  session_status?: string | null;
  session_author?: string | null;
  batch_id?: string | number | null;
  course_id?: string | number | null;
  track_id?: string | number | null;
  session_starttime?: string | null;
  recording_status?: string;
  streaming_session_id?: number | null;
  streaming_status?: string | null;
  mismatch?: boolean;
}

export interface StaleLmsSession {
  lms_session_id: string;
  session_name?: string | null;
  session_status?: string | null;
  session_author?: string | null;
  expected_room_name?: string;
  session_starttime?: string | null;
  mismatch?: boolean;
  reason?: string;
}

export interface LiveKitMonitorSnapshot {
  ok: boolean;
  collected_at: string;
  summary: MonitorSummary;
  host: HostMetrics;
  rooms: MonitorRoomRow[];
  stale_lms_sessions: StaleLmsSession[];
  livekit_url?: string | null;
}

export interface RoomParticipant {
  identity: string;
  name?: string | null;
  role?: string | null;
  metadata?: Record<string, unknown>;
}

export interface RoomParticipantsResponse {
  ok: boolean;
  room_name: string;
  participant_count?: number;
  participants: RoomParticipant[];
  error?: string;
  collected_at?: string;
}

function looksEncryptedCipher(value: unknown): value is string {
  return typeof value === "string" && value.startsWith("gAAAAA");
}

function unwrapData<T>(payload: unknown): T {
  let cur: unknown = payload;
  if (cur && typeof cur === "object" && "data" in cur) {
    const nested = (cur as { data: unknown }).data;
    if (looksEncryptedCipher(nested)) {
      cur = decryptJSON(nested);
    } else if (nested && typeof nested === "object") {
      cur = nested;
    }
  }
  if (looksEncryptedCipher(cur)) {
    cur = decryptJSON(cur);
  }
  return cur as T;
}

export async function fetchLiveKitMonitor(): Promise<LiveKitMonitorSnapshot> {
  const res = await authService.get("/admin/livekit/monitor/");
  return unwrapData<LiveKitMonitorSnapshot>(res.data);
}

export async function fetchLiveKitRoomParticipants(
  roomName: string,
): Promise<RoomParticipantsResponse> {
  const encoded = encodeURIComponent(roomName);
  const res = await authService.get(
    `/admin/livekit/monitor/rooms/${encoded}/participants/`,
  );
  return unwrapData<RoomParticipantsResponse>(res.data);
}
